import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Apiurl from "../../environment/Apiurl";

function UpdateSalaryDetails() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    staff_id: "",
    first_name: "",
    mobile_no: "",
    department: "",
    effective_month: "",
    basic_pay: "",
    hra: "",
    other_allowances: "",
    pf_deduction: "",
    professional_tax: "",
    other_deductions: "",
    status: "Active",
    notes: "",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const fetchSalaryDetail = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/salary-details/${id}`);
      setFormData(response.data || {});
    } catch (error) {
      console.error("Error fetching salary record:", error.message);
      setError("Failed to load salary record.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchSalaryDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const num = (v) => parseFloat(v) || 0;
  const totalAllowances = num(formData.hra) + num(formData.other_allowances);
  const grossSalary = num(formData.basic_pay) + totalAllowances;
  const totalDeductions =
    num(formData.pf_deduction) +
    num(formData.professional_tax) +
    num(formData.other_deductions);
  const netSalary = grossSalary - totalDeductions;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${Apiurl}/salary-details/${id}`, {
        ...formData,
        basic_pay: num(formData.basic_pay),
        hra: num(formData.hra),
        other_allowances: num(formData.other_allowances),
        pf_deduction: num(formData.pf_deduction),
        professional_tax: num(formData.professional_tax),
        other_deductions: num(formData.other_deductions),
      });
      navigate("/hr-management/salary-details");
    } catch (error) {
      console.error("Error updating salary details:", error.message);
      setError("Failed to update salary details. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="container-fluid py-4">Loading...</div>;
  }

  return (
    <div className="container-fluid py-4">
      <div className="card p-4">
        <h4 className="fw-bold mb-4">Update Salary Details</h4>

        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label">Staff ID</label>
              <input
                type="text"
                className="form-control"
                value={formData.staff_id || ""}
                disabled
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Department</label>
              <input
                type="text"
                className="form-control"
                value={formData.department || ""}
                disabled
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Mobile No.</label>
              <input
                type="text"
                className="form-control"
                value={formData.mobile_no || ""}
                disabled
              />
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label">Effective Month</label>
              <input
                type="month"
                className="form-control"
                name="effective_month"
                value={formData.effective_month || ""}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                name="status"
                value={formData.status || "Active"}
                onChange={handleChange}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          <hr />
          <h6 className="fw-bold">Earnings</h6>
          <div className="row">
            <div className="col-md-3 mb-3">
              <label className="form-label">Basic Pay</label>
              <input
                type="number"
                className="form-control"
                name="basic_pay"
                value={formData.basic_pay ?? ""}
                onChange={handleChange}
                required
                min="0"
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">HRA</label>
              <input
                type="number"
                className="form-control"
                name="hra"
                value={formData.hra ?? ""}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Other Allowances</label>
              <input
                type="number"
                className="form-control"
                name="other_allowances"
                value={formData.other_allowances ?? ""}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Gross Salary</label>
              <input
                type="number"
                className="form-control"
                value={grossSalary}
                disabled
              />
            </div>
          </div>

          <hr />
          <h6 className="fw-bold">Deductions</h6>
          <div className="row">
            <div className="col-md-3 mb-3">
              <label className="form-label">PF Deduction</label>
              <input
                type="number"
                className="form-control"
                name="pf_deduction"
                value={formData.pf_deduction ?? ""}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Professional Tax</label>
              <input
                type="number"
                className="form-control"
                name="professional_tax"
                value={formData.professional_tax ?? ""}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Other Deductions</label>
              <input
                type="number"
                className="form-control"
                name="other_deductions"
                value={formData.other_deductions ?? ""}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Total Deductions</label>
              <input
                type="number"
                className="form-control"
                value={totalDeductions}
                disabled
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label fw-bold">Net Salary</label>
              <input
                type="number"
                className="form-control fw-bold"
                value={netSalary}
                disabled
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Notes</label>
            <textarea
              className="form-control"
              name="notes"
              rows="2"
              value={formData.notes || ""}
              onChange={handleChange}
            />
          </div>

          <div className="d-flex gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate("/hr-management/salary-details")}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Update Salary Details"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UpdateSalaryDetails;
